'use client'
import Image from 'next/image'
import LifecycleLink from '@/components/common/LifecycleLink'
import clsx from 'clsx'
import { Avatar, Tag, Tooltip } from 'antd'
import { motion } from 'framer-motion'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styles from './Character.module.css'
import {
  Character,
  CharacterStats,
  CharacterVoiceParam,
  StoryGetRes,
} from 'Types/character'
import { User } from 'Types/user'
import {
  CardVoiceIcon,
  Create18PlusIcon,
  HomeStarIcon,
  HomeVHoverIcon,
  HomeVIcon,
  MemoryIcon,
  MessageIcon,
  TagEventIcon,
} from 'Icons'
import { formatNumber } from 'Utils/func'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import LoadingImg from '@/icons/LoadingImg'
import { VoicePlayingIcon } from 'Comps/svg/VoicePlayingIcon'
import { showCharNumState, userState } from 'Store/user'
import { loginModalOpenState } from 'Store/login'
import {
  getAnimatedImageDisplayUrl,
  transformImage,
  isVideoUrl,
} from '@/utils/img'
import { DEFAULT_CHARACTER_LIST_IMAGE_WIDTH } from '@/constant/img'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import { StoryIcon } from '@/icons/creatStoryIcon'
import { TagID } from '@/types/tag'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getPlatform } from '@/utils/platform'
import { resolveTagWatermark } from '@/utils/tagDisplay'
import HomeCardVideo from '@/components/home/HomeCardVideo'
import { evictHomeMediaFromPersistentCache } from '@/packages/sw-prefetch/src/prefetch/homeMediaCacheRegistration'

type CharacterItemProps = {
  item: StoryGetRes
  fromScreen?: string
  logparams?: {
    gender: string
    selectedTags: TagID[] | string[]
    sorting?: string
  }
  onMediaReady?: () => void
  /** 首行卡片媒体提优先级（eager + fetchPriority=high），见 CharacterHomeItem 同名 prop */
  mediaPriority?: boolean
}
export default function CharacterHomeItem({
  item,
  fromScreen,
  logparams,
  onMediaReady,
  mediaPriority = false,
}: CharacterItemProps) {
  const hoverExitDelayMs = 500
  const { lang, t } = useI18n()
  const { trackEvent } = useQuickTracking()
  const platform = getPlatform()
  const [isHoverExitDelayed, setIsHoverExitDelayed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const bgRef = useRef<HTMLDivElement>(null)
  const hoverExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const user = useRecoilValue(userState)
  const isVideoMedia = isVideoUrl(item.animated_image_url)
  // 非视频的 animated_image_url 视为动图，单独用原生 img 渲染
  const isAnimatedImageMedia = !!item.animated_image_url && !isVideoMedia
  const imageMediaUrl = getAnimatedImageDisplayUrl(
    item.animated_image_url,
    item.image_url,
    DEFAULT_CHARACTER_LIST_IMAGE_WIDTH,
    90
  )
  const primaryMediaUrl = isVideoMedia ? item.animated_image_url : imageMediaUrl
  const mediaReadyRef = useRef({
    key: '',
    ready: false,
  })
  const mediaReadyKey = `${item.story_id}:${primaryMediaUrl}`
  if (mediaReadyRef.current.key !== mediaReadyKey) {
    mediaReadyRef.current = {
      key: mediaReadyKey,
      ready: false,
    }
  }
  const handleMediaReady = useCallback(() => {
    if (mediaReadyRef.current.ready) return
    mediaReadyRef.current.ready = true
    onMediaReady?.()
  }, [onMediaReady])
  const handleMediaError = useCallback(() => {
    evictHomeMediaFromPersistentCache(primaryMediaUrl)
    handleMediaReady()
  }, [handleMediaReady, primaryMediaUrl])

  useEffect(() => {
    if (!primaryMediaUrl) handleMediaReady()
  }, [handleMediaReady, primaryMediaUrl])
  const handleHoverStart = () => {
    if (hoverExitTimerRef.current) {
      clearTimeout(hoverExitTimerRef.current)
      hoverExitTimerRef.current = null
    }

    setIsHoverExitDelayed(true)

    const text = textRef.current
    const container = containerRef.current
    if (!text || !container) return
    const textToTop = text.getBoundingClientRect().top
    const containerToTop = container.getBoundingClientRect().top
    // 行高是16px，为了在行数非常多的清空下，显示整数行的内容
    textRef.current.style.maxHeight = `${(((textToTop - containerToTop + 7) / 16) | 0) * 16}px`
  }
  const handleHoverEnd = () => {
    if (hoverExitTimerRef.current) {
      clearTimeout(hoverExitTimerRef.current)
    }

    hoverExitTimerRef.current = setTimeout(() => {
      const text = textRef.current
      hoverExitTimerRef.current = null
      setIsHoverExitDelayed(false)
      if (!text) return
      text.style.maxHeight = '3rem'
    }, hoverExitDelayMs)
  }
  // 直接使用接口返回的 tags 数组判断，不依赖 TagMap
  const watermarkUrl = resolveTagWatermark(item.tags)
  useEffect(() => {
    trackEvent(
      'character_page_exposure',
      {
        scene: fromScreen,
        type: 'character',
        characterId: item.story_id,
        creatorId: item.creator_id,
        nsfw: !!item.nsfw,
        isStory: false,
        filter: logparams,
        banner: '',
        platform,
      },
      'EXP'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.story_id])
  useEffect(() => {
    return () => {
      if (hoverExitTimerRef.current) {
        clearTimeout(hoverExitTimerRef.current)
      }
    }
  }, [])
  return (
    <div
      className={clsx(
        'relative w-full overflow-hidden rounded-lg border border-tertiary-border bg-secondary-bg pt-[167.57%]'
      )}
    >
      <LifecycleLink
        href={withLocale(`/chat/story${item.story_id}`, lang)}
        prefetch
        className="absolute inset-0"
        onClick={() => {
          trackEvent(
            'character_page_click',
            {
              scene: fromScreen,
              type: 'character',
              characterId: item.story_id,
              creatorId: item.creator_id,
              nsfw: !!item.nsfw,
              isStory: false,
              filter: logparams,
              banner: '',
              platform,
            },
            'EXP'
          )
        }}
      >
        {watermarkUrl && (
          <img
            style={{
              position: 'absolute',
              left: '5px',
              top: '5px',
              zIndex: 1,
              width: '100px',
              height: '100px',
              objectFit: 'contain',
            }}
            src={watermarkUrl}
            alt=""
          />
        )}

        {isVideoMedia ? (
          <HomeCardVideo
            src={item.animated_image_url}
            eager={mediaPriority}
            onLoadedData={handleMediaReady}
            onError={handleMediaError}
            className={clsx('absolute inset-0 h-full w-full object-cover')}
          />
        ) : isAnimatedImageMedia ? (
          // GIF/动图不用 next/image，避免内部 src/srcset 更新导致动画回到首帧
          <img
            className={clsx('absolute inset-0 h-full w-full object-cover')}
            src={imageMediaUrl}
            alt={item.title}
            loading={mediaPriority ? 'eager' : 'lazy'}
            fetchPriority={mediaPriority ? 'high' : undefined}
            onLoad={handleMediaReady}
            onError={handleMediaError}
          />
        ) : (
          <Image
            unoptimized
            className={clsx('object-cover')}
            src={imageMediaUrl}
            alt={item.title}
            fill={true}
            priority={mediaPriority}
            onLoad={handleMediaReady}
            onError={handleMediaError}
          />
        )}
      </LifecycleLink>

      <div className="pointer-events-none absolute inset-0 max-md:hidden">
        {/* 顶部渐变效果，用于让水印更清晰 */}
        {watermarkUrl && (
          <div className="absolute left-0 right-0 top-0 h-[30%] bg-gradient-to-b from-black/50 to-black/0" />
        )}
        {item.nsfw && (
          <div className="absolute right-3 top-3">
            <Create18PlusIcon />
          </div>
        )}
        <div ref={containerRef} className="flex h-full flex-col justify-end">
          <div
            ref={bgRef}
            className={clsx(
              'flex max-h-[100%] min-h-[50%] flex-col justify-end bg-gradient-to-b from-black/0 to-black pt-4 text-white transition-all duration-300'
            )}
          >
            <LifecycleLink
              href={withLocale(`/chat/story${item.story_id}`, lang)}
              prefetch
              onMouseLeave={handleHoverEnd}
              className={clsx(
                'z-1 pointer-events-auto relative flex h-full flex-col justify-end px-3.5',
                styles['hover-parent'],
                isHoverExitDelayed && styles['hover-parent-active']
              )}
            >
              <div className="flex h-5 w-14 flex-row items-center justify-center rounded-full bg-black/30 pr-1">
                <StoryIcon className="" width={16} height={16} />
                <span className="text-[10px]">{t('Story')}</span>
              </div>
              <div className="flex items-end gap-2">
                <span className="truncate text-base font-medium leading-6">
                  {item.title}
                </span>
              </div>
              <motion.div onMouseEnter={handleHoverStart}>
                <div className="mt-[5px] overflow-hidden pb-2.5 text-xs">
                  <div
                    ref={textRef}
                    className={clsx(styles['ellipsis-3-line'], 'text-white/60')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.summary}
                  </div>
                </div>
              </motion.div>
            </LifecycleLink>
            <svg width="100%" height="1">
              <line
                x1="0"
                y1="0"
                x2="100%"
                y2="0"
                style={{
                  stroke: 'rgba(255, 255, 255, 0.3)',
                  strokeWidth: 2,
                  strokeDasharray: '3, 3',
                }}
              />
            </svg>
            <div
              className="pointer-events-auto flex h-[40px] cursor-auto items-center justify-between px-3.5 py-2.5 text-sm text-white"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            >
              <LifecycleLink
                href={withLocale(
                  `/profile/${item.creator_id}?tab=Characters`,
                  lang
                )}
                prefetch
                className="flex min-w-0 flex-1 items-center gap-1"
              >
                <Avatar
                  src={transformImage(item.creator_avatar_url, 56)}
                  alt={t('Creator')}
                  size={26}
                />
                <span className="w-fit flex-1 truncate text-sm text-white">
                  @{item.creator_nickname}
                </span>
              </LifecycleLink>

              <div className="flex gap-2">
                <div className="flex items-center gap-1 text-xs">
                  <MessageIcon />
                  {formatNumber(Number(item.total_messages) || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
